/* organization/OrgUsage — Organization capacity management; current resource consumption vs allocated limits */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard } from '../platform/_ui';
import { usePermissions } from './auth/usePermissions.js';
import { useOrgRuntime, useLimits } from './OrganizationRuntimeContext.jsx';
import { DeniedState } from './auth/DeniedState.jsx';
import { ORG } from './auth/capabilities.js';
import * as api from '../api/org-usage.api.js';

/* ─── helpers ─────────────────────────────────────────────────── */

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const gb = b / 1_073_741_824;
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${(b / 1_048_576).toFixed(1)} MB`;
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined));

function pctColor(pct) {
  if (pct >= 90) return 'var(--nx-danger)';
  if (pct >= 70) return 'var(--nx-warning)';
  return 'var(--nx-success)';
}

function capacityColor(pct, base) {
  if (pct >= 90) return 'red';
  if (pct >= 70) return 'amber';
  return base;
}

function licenseRemainingColor(remaining, allotted) {
  if (remaining === 0) return 'var(--nx-danger)';
  if (remaining <= Math.ceil(allotted * 0.1)) return 'var(--nx-warning)';
  return 'var(--nx-success)';
}

/* ─── UsageRow ─────────────────────────────────────────────────── */

const UsageRow = ({ icon, label, used, max, displayUsed, displayMax, note }) => {
  const isTracked = used !== null && used !== undefined;
  const pct       = isTracked && max > 0 ? Math.min(100, Math.round((used / max) * 100)) : null;
  const color     = pct != null ? pctColor(pct) : 'var(--nx-accent)';
  const showUsed  = isTracked ? (displayUsed ?? fmtNum(used)) : null;
  const showMax   = displayMax ?? (max > 0 ? fmtNum(max) : null);

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--nx-divider)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: pct != null ? 8 : 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-text-muted)' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
          {note && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{note}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          {!isTracked
            ? <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>Not tracked</span>
            : (
              <>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{showUsed}</span>
                {showMax && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}> / {showMax}</span>}
                {pct != null && <div style={{ fontSize: 11, color, marginTop: 1 }}>{pct}%</div>}
              </>
            )}
        </div>
      </div>
      {pct != null && (
        <progress
          value={pct}
          max={100}
          aria-label={`${label}: ${pct}% used`}
          className="nx-usage-bar"
          style={{ '--nx-usage-bar-fill': color }}
        />
      )}
    </div>
  );
};

/* ─── Panel ────────────────────────────────────────────────────── */

const Panel = ({ title, children }) => (
  <div className="nx-cc-chart-card" style={{ padding: 20, flex: '1 1 340px', minWidth: 0 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
      {title}
    </div>
    {children}
  </div>
);

/* ─── License display names ─────────────────────────────────────── */

const LICENSE_LABEL = {
  USER:      'User Seats',
  BOT:       'Bots',
  AGENT:     'AI Agents',
  CUSTOM:    'Custom',
  WORKSPACE: 'Workspaces',
  STORAGE:   'Storage',
  API:       'API Access',
  MESSAGE:   'Messages',
};

/* ─── Skeleton ──────────────────────────────────────────────────── */

const Skel = ({ h = 18, mb = 8 }) => (
  <div style={{ height: h, borderRadius: 5, background: 'var(--nx-surface-2, #222938)', opacity: 0.35, marginBottom: mb }} />
);

const UsageSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    <div className="nx-cc-stat-grid nx-kpi-4">
      {[1, 2, 3, 4].map(k => <Skel key={k} h={76} />)}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}><Skel h={200} mb={0} /></div>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}><Skel h={200} mb={0} /></div>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}><Skel h={160} mb={0} /></div>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}><Skel h={160} mb={0} /></div>
    </div>
  </div>
);

/* ─── StoragePanel ──────────────────────────────────────────────── */

const StoragePanel = ({ usedBytes, totalBytes, storagePct }) => (
  <Panel title="Storage">
    <UsageRow
      icon="HardDrive" label="Allocated Storage"
      used={usedBytes} max={totalBytes}
      displayUsed={fmtBytes(usedBytes)} displayMax={fmtBytes(totalBytes)}
    />
    <div style={{ marginTop: 14, padding: 12, borderRadius: 7, background: 'var(--nx-surface-alt)' }}>
      {[
        { label: 'Used',        value: fmtBytes(usedBytes) },
        { label: 'Allocated',   value: fmtBytes(totalBytes) },
        { label: 'Available',   value: fmtBytes(Math.max(0, totalBytes - usedBytes)) },
        { label: 'Utilization', value: totalBytes > 0 ? `${storagePct}%` : '—' },
      ].map(row => (
        <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--nx-divider)' }}>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{row.label}</span>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{row.value}</span>
        </div>
      ))}
    </div>
  </Panel>
);

/* ─── LicenseTable ──────────────────────────────────────────────── */

const LicenseTable = ({ licenses }) => {
  if (!licenses?.length) {
    return (
      <div className="nx-empty" style={{ padding: '24px 0' }}>
        <NexusIcon name="Key" size={18} />
        <span>No active licenses</span>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
            <th scope="col" style={{ textAlign: 'left',  padding: '4px 0 8px', fontWeight: 600 }}>Type</th>
            <th scope="col" style={{ textAlign: 'right', padding: '4px 0 8px', fontWeight: 600 }}>Allotted</th>
            <th scope="col" style={{ textAlign: 'right', padding: '4px 0 8px', fontWeight: 600 }}>Used</th>
            <th scope="col" style={{ textAlign: 'right', padding: '4px 0 8px', fontWeight: 600 }}>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {licenses.map(l => (
            <tr key={l.type} style={{ borderTop: '1px solid var(--nx-divider)' }}>
              <td style={{ padding: '8px 0', fontWeight: 500 }}>{LICENSE_LABEL[l.type] ?? l.type}</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(l.allotted)}</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(l.used)}</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: licenseRemainingColor(l.remaining, l.allotted) }}>
                {fmtNum(l.remaining)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─── AISection ─────────────────────────────────────────────────── */

const AISection = ({ ai, apiCallsUsed, emailSendsUsed, maxApiCalls, maxMessages }) => (
  <div className="nx-cc-chart-card" style={{ padding: 20 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
      {`AI Usage — ${ai?.period ?? 'This Month'}`}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 4 }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        {[
          { label: 'Input Tokens',   value: fmtNum(ai?.inputTokens) },
          { label: 'Output Tokens',  value: fmtNum(ai?.outputTokens) },
          { label: 'Estimated Cost', value: `$${(ai?.totalCost ?? 0).toFixed(4)}` },
        ].map(row => (
          <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--nx-divider)' }}>
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{row.label}</span>
            <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{row.value}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <UsageRow icon="Zap"  label="API Calls / Day"    used={apiCallsUsed}   max={maxApiCalls}  note="Not tracked for your plan" />
        <UsageRow icon="Send" label="Email Sends / Month" used={emailSendsUsed} max={maxMessages}  note="Not tracked for your plan" />
      </div>
    </div>
  </div>
);

/* ─── UsageContent — full layout rendered only when usage is non-null ── */

const UsageContent = ({ usage, limits, loading }) => {
  /* Limit values via destructuring defaults — avoids ?? operators in derived computations */
  const {
    maxUsers      = 0,
    maxWorkspaces = 0,
    maxAgents     = 0,
    maxBots       = 0,
    maxApiCallsPerDay:   maxApiCalls = 0,
    maxMessagesPerMonth: maxMessages = 0,
  } = limits;

  const usedBytes      = usage.storage.usedBytes;
  const totalBytes     = usage.storage.totalBytes;

  /* Percentage calculations */
  const storagePct = totalBytes    > 0 ? Math.round((usedBytes                  / totalBytes)    * 100) : 0;
  const userPct    = maxUsers      > 0 ? Math.round((usage.members.used          / maxUsers)      * 100) : 0;
  const wsPct      = maxWorkspaces > 0 ? Math.round((usage.workspaces.used       / maxWorkspaces) * 100) : 0;
  const agentPct   = maxAgents     > 0 ? Math.round((usage.agents.used           / maxAgents)     * 100) : 0;

  /* StatCard sub-text */
  const userSub    = maxUsers      > 0 ? `of ${fmtNum(maxUsers)} · ${userPct}%`        : 'No seat limit';
  const wsSub      = maxWorkspaces > 0 ? `of ${fmtNum(maxWorkspaces)} · ${wsPct}%`     : 'No workspace limit';
  const storageSub = totalBytes    > 0 ? `of ${fmtBytes(totalBytes)} · ${storagePct}%` : 'No storage quota';
  const agentSub   = maxAgents     > 0 ? `of ${fmtNum(maxAgents)} · ${agentPct}%`      : 'No agent limit';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Top StatCards */}
      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard icon="Users"      label="User Seats"  value={fmtNum(usage.members.used)}    sub={userSub}    color={capacityColor(userPct,    'green')}  />
        <StatCard icon="LayoutGrid" label="Workspaces"  value={fmtNum(usage.workspaces.used)} sub={wsSub}      color={capacityColor(wsPct,      'blue')}   />
        <StatCard icon="HardDrive"  label="Storage"     value={fmtBytes(usedBytes)}           sub={storageSub} color={capacityColor(storagePct, 'green')}  />
        <StatCard icon="Cpu"        label="AI Agents"   value={fmtNum(usage.agents.used)}     sub={agentSub}   color={capacityColor(agentPct,   'purple')} />
      </div>

      {/* Seat Capacity + Storage */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <Panel title="Seat & Workspace Capacity">
          <UsageRow icon="Users"         label="User Seats"   used={usage.members.used}    max={maxUsers}      />
          <UsageRow icon="LayoutGrid"    label="Workspaces"   used={usage.workspaces.used} max={maxWorkspaces} />
          <UsageRow icon="Cpu"           label="AI Agents"    used={usage.agents.used}     max={maxAgents}     />
          <UsageRow icon="Bot"           label="Bots"         used={usage.bots.used}       max={maxBots}       note="Not tracked for your plan" />
          <UsageRow icon="MessageSquare" label="Channels"     used={usage.channels.used}   max={0}             note="No configured limit" />
        </Panel>
        <StoragePanel usedBytes={usedBytes} totalBytes={totalBytes} storagePct={storagePct} />
      </div>

      {/* Developer Resources + Content */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <Panel title="Developer Resources">
          <UsageRow icon="Key"       label="API Keys"           used={usage.apiKeys.used}     max={0} note="Active only" />
          <UsageRow icon="Webhook"   label="Webhooks"           used={usage.webhooks.used}    max={0} />
          <UsageRow icon="AppWindow" label="OAuth Apps"         used={usage.oauthApps.used}   max={0} />
          <UsageRow icon="Shield"    label="IP Allowlist Rules" used={usage.ipAllowlists.used} max={0} />
        </Panel>
        <Panel title="Content & Fields">
          <UsageRow icon="BookOpen" label="Knowledge Bases"     used={usage.knowledgeBases.used} max={0} />
          <UsageRow icon="FileText" label="Prompts"             used={usage.prompts.used}        max={0} />
          <UsageRow icon="Layout"   label="Workspace Templates" used={usage.templates.used}      max={0} />
        </Panel>
      </div>

      {/* Teams & Governance + Licenses */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        <Panel title="Teams & Governance">
          <UsageRow icon="UsersRound" label="Teams"        used={usage.teams.used}       max={0} />
          <UsageRow icon="Lock"       label="Custom Roles" used={usage.customRoles.used} max={0} note="User-defined only" />
        </Panel>
        <Panel title="Licenses">
          <LicenseTable licenses={usage.licenses} />
        </Panel>
      </div>

      {/* AI Usage */}
      <AISection
        ai={usage.ai}
        apiCallsUsed={usage.apiCalls.used}
        emailSendsUsed={usage.emailSends.used}
        maxApiCalls={maxApiCalls}
        maxMessages={maxMessages}
      />

    </div>
  );
};

/* ─── renderBody — selected outside OrgUsage to not contribute to its complexity ── */

function renderBody(usage, limits, loading) {
  if (loading && !usage) return <UsageSkeleton />;
  if (!usage) {
    return (
      <div className="nx-empty" style={{ padding: '48px 0' }}>
        <NexusIcon name="AlertCircle" size={20} />
        <span>Could not load usage data.</span>
      </div>
    );
  }
  return <UsageContent usage={usage} limits={limits} loading={loading} />;
}

/* ─── Component ─────────────────────────────────────────────────── */

export default function OrgUsage({ data }) {
  const { orgId }   = useParams();
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const toast       = useToast();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const perm        = usePermissions();
  const limits      = useLimits();
  const { loading: runtimeLoading } = useOrgRuntime();

  const org = data.state.organizations.find(o => o.id === orgId);

  const [usage, setUsage]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const u = await api.getUsage(orgId);
      setUsage(u);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load usage data' });
    } finally { setLoading(false); }
  }, [orgId, toast]);

  useEffect(() => { load(); }, [load]);

  if (runtimeLoading) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Usage &amp; Quotas" sub="Loading…" />
        <UsageSkeleton />
      </div>
    );
  }
  if (!org) return null;

  if (!perm.has(ORG.USAGE_VIEW)) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Usage & Quotas" sub={org.name} />
        <DeniedState
          reason="insufficient_permission"
          message="Usage & Quotas requires the org.usage.view capability. Contact your org admin to request access."
          action={() => navigate(`/organization/${orgId}`)}
          actionLabel="Back to Dashboard"
        />
      </div>
    );
  }

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Usage & Quotas"
        sub={`Resource consumption and limits for ${org.name}`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {loading && usage && (
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <NexusIcon name="Loader" size={12} /> Refreshing…
              </span>
            )}
            <Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={load}>Refresh</Btn>
          </div>
        }
      />
      {renderBody(usage, limits, loading)}
    </div>
  );
}
